import { useCallback, useEffect, useState } from "react";
import {
  Banner, Card, CardHead, Empty, Label, Modal, Pill, Skeleton, Toasts, useToasts,
} from "../components/ui";
import { IconApprovals } from "../components/icons";

interface ApprovalRow {
  id: string;
  kind: string;
  summary: Record<string, string | null>;
  reasons: string[];
  requires_second: boolean;
  first_approver: string | null;
  status: string;
  decision_note: string | null;
  created_at: string;
}

const auth = () => ({
  authorization: `Bearer ${sessionStorage.getItem("otc.session") ?? ""}`,
  "content-type": "application/json",
});

type Pending = { row: ApprovalRow; action: "approve" | "reject" };

/**
 * The summary arrives as free-form strings, and the amounts in it are the
 * whole point of the screen — an unseparated "346500.00 EUR" is one glance
 * away from being read as 34,650 or 3,465,000. Anything that parses as a
 * number gets thousands separators; everything else is passed through
 * untouched, so a pair or a reference is never mangled.
 */
function pretty(v: string | null): string {
  if (v === null || v === "") return "—";
  const m = /^(~?\s*)(-?\d+(?:\.\d+)?)(\s*[A-Za-z_]{2,12})?$/.exec(v.trim());
  if (!m) return v;
  const [, prefix, digits, unit] = m;
  const n = Number(digits);
  if (!Number.isFinite(n)) return v;
  const dp = digits!.includes(".") ? Math.min(8, digits!.split(".")[1]!.length) : 0;
  return `${prefix ?? ""}${n.toLocaleString("en-US", { minimumFractionDigits: dp, maximumFractionDigits: dp })}${unit ?? ""}`;
}

export function Approvals({ onChange }: { onChange?: () => void }) {
  const [rows, setRows] = useState<ApprovalRow[] | null>(null);
  const [error, setError] = useState("");
  const [confirm, setConfirm] = useState<Pending | null>(null);
  const [busy, setBusy] = useState(false);
  const toast = useToasts();

  const load = useCallback(async () => {
    const res = await fetch("/api/approvals", { headers: auth() });
    if (res.ok) {
      setRows((await res.json()).approvals);
      setError("");
    } else setError("Could not load approvals.");
  }, []);

  useEffect(() => {
    void load();
    const t = setInterval(() => void load(), 20_000);
    return () => clearInterval(t);
  }, [load]);

  // Decisions move money and are audited, so each one goes through a real
  // dialog that names the order and forces a reason on rejection. The previous
  // build used window.prompt() and window.alert() here — unstyleable,
  // unlabelled for screen readers, and impossible to validate before it
  // closes, which is exactly the wrong shape for a four-eyes control.
  async function act({ row, action }: Pending, note: string) {
    setBusy(true);
    try {
      const res = await fetch(`/api/approvals/${row.id}/${action}`, {
        method: "POST",
        headers: auth(),
        body: JSON.stringify(note ? { note } : {}),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        toast.push("error", `Could not ${action}: ${body?.message?.code ?? body?.message ?? res.status}`);
      } else {
        toast.push("ok", action === "approve" ? "Approved." : "Rejected.");
      }
      await load();
      onChange?.();
    } finally {
      setBusy(false);
      setConfirm(null);
    }
  }

  if (!rows && !error) return <Skeleton rows={5} />;

  const pending = (rows ?? []).filter((r) => r.status === "pending");
  const decided = (rows ?? []).filter((r) => r.status !== "pending");

  return (
    <>
      <p className="page-intro">
        Orders and payouts the rules engine held for a human decision. Everything here is audited, and a
        four-eyes item needs two different approvers.
      </p>

      {error && <Banner tone="error">{error}</Banner>}

      {pending.length === 0 ? (
        <Card>
          <Empty icon={<IconApprovals size={20} />} title="Nothing waiting"
                 body="Orders flow straight through unless they trip a limit or a compliance rule." />
        </Card>
      ) : (
        <div className="grid" style={{ maxWidth: 720 }}>
          {pending.map((r) => (
            <Card key={r.id} pad={false} className="enter">
              <CardHead
                title={r.kind.replaceAll("_", " ")}
                right={<span className="t-label">{new Date(r.created_at).toLocaleString()}</span>}
              />
              <div className="card-pad">
                <dl style={{ display: "grid", gridTemplateColumns: "auto 1fr", gap: "7px 18px", margin: 0 }}>
                  {Object.entries(r.summary).map(([k, v]) => (
                    <div key={k} style={{ display: "contents" }}>
                      <dt className="dim t-sm" style={{ textTransform: "capitalize" }}>{k.replaceAll("_", " ")}</dt>
                      <dd className="num" style={{ margin: 0, fontWeight: 500 }}>{pretty(v)}</dd>
                    </div>
                  ))}
                </dl>

                <div style={{ marginTop: 16, display: "flex", gap: 6, flexWrap: "wrap" }}>
                  {r.reasons.map((reason) => (
                    <Pill key={reason} tone="warn">{reason.replaceAll("_", " ")}</Pill>
                  ))}
                </div>

                {r.requires_second && (
                  <div style={{ marginTop: 14 }}>
                    <Banner tone="info">
                      Four-eyes: two different approvers required
                      {r.first_approver ? ". First approval given, awaiting the second." : "."}
                    </Banner>
                  </div>
                )}

                <div style={{ display: "flex", gap: 8, marginTop: 18 }}>
                  <button type="button" className="btn btn-primary" disabled={busy}
                          onClick={() => setConfirm({ row: r, action: "approve" })}>
                    Approve
                  </button>
                  <button type="button" className="btn btn-secondary" disabled={busy}
                          onClick={() => setConfirm({ row: r, action: "reject" })}>
                    Reject
                  </button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {decided.length > 0 && (
        <div className="section">
          <Label>Recent decisions</Label>
          <div style={{ height: 12 }} />
          <Card pad={false}>
            <div className="table-wrap flush">
              <table>
                <thead>
                  <tr><th>When</th><th>Kind</th><th>Detail</th><th>Outcome</th></tr>
                </thead>
                <tbody>
                  {decided.slice(0, 20).map((r) => (
                    <tr key={r.id}>
                      <td className="dim num" style={{ whiteSpace: "nowrap" }}>
                        {new Date(r.created_at).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}
                      </td>
                      <td style={{ textTransform: "capitalize" }}>{r.kind.replaceAll("_", " ")}</td>
                      <td className="dim num">
                        {[r.summary["client"], r.summary["amount"]].filter(Boolean).join(", ") || "\u2014"}
                      </td>
                      <td>
                        <Pill tone={r.status === "approved" ? "good" : "bad"} dot>{r.status}</Pill>
                        {r.decision_note && <div className="dim t-sm" style={{ marginTop: 3 }}>{r.decision_note}</div>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </div>
      )}

      {confirm && (
        <Modal
          title={confirm.action === "approve" ? "Approve this request?" : "Reject this request?"}
          tone={confirm.action === "approve" ? "primary" : "danger"}
          confirmLabel={confirm.action === "approve" ? "Approve" : "Reject"}
          requireNote={confirm.action === "reject"}
          notePlaceholder={confirm.action === "reject" ? "Reason for rejection (required)" : "Note (optional)"}
          body={
            <>
              <div style={{ textTransform: "capitalize", color: "var(--text)", fontWeight: 500 }}>
                {confirm.row.kind.replaceAll("_", " ")}
              </div>
              <div className="t-sm num" style={{ marginTop: 4 }}>
                {Object.entries(confirm.row.summary).map(([k, v]) => `${k.replaceAll("_", " ")}: ${pretty(v)}`).join(", ")}
              </div>
            </>
          }
          onCancel={() => setConfirm(null)}
          onConfirm={(note) => void act(confirm, note)}
        />
      )}

      <Toasts items={toast.items} onDone={toast.dismiss} />
    </>
  );
}
